import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export type PermissionResourceType = 'ap_invoice' | 'ar_invoice' | 'payment' | 'expense';

export interface MemberPermission {
  id: string;
  userId: string;
  resourceType: string;
  entityId: string | null;
  canView: boolean;
  canCreate: boolean;
  canApprove: boolean;
  canPublish: boolean;
}

export interface UpsertGrantInput {
  userId: string;
  resourceType: PermissionResourceType;
  entityId?: string | null;
  canView?: boolean;
  canCreate?: boolean;
  canApprove?: boolean;
  canPublish?: boolean;
}

/** Owner/admin-only: list + edit member permission grants for a workspace.
 *  Backed by GET/PUT/DELETE /workspaces/:wsId/permissions. */
export function usePermissions(wsId: string | undefined, enabled = true) {
  const qc = useQueryClient();

  const list = useQuery<MemberPermission[]>({
    queryKey: ['permissions', wsId],
    enabled: !!wsId && enabled,
    queryFn: async () => (await api.get<MemberPermission[]>(`/workspaces/${wsId}/permissions`)).data,
  });

  const upsert = useMutation({
    mutationFn: async (input: UpsertGrantInput) =>
      (await api.put(`/workspaces/${wsId}/permissions`, input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['permissions', wsId] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/permissions/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['permissions', wsId] }),
  });

  return { list, upsert, remove };
}
